<template>
  <div class="bm-breadcrumb-list pt-2 pt-md-0 no-print">
    <template v-if="screenSize.mdAndUp">
      <template v-for="(breadcrumb, index) in breadcrumbs">
        <span v-if="index > 0" v-bind:key="breadcrumb.text + index + '_1'">
          &gt;
        </span>
        <router-link
          v-if="breadcrumb.location"
          v-bind:key="breadcrumb.text + index + '_2'"
          :to="breadcrumb.location"
          class="bm-breadcrumb-item"
          >{{ breadcrumb.text }}
        </router-link>
        <span
          v-else
          v-bind:key="breadcrumb.text + index + '_3'"
          class="bm-breadcrumb-item"
        >
          {{ breadcrumb.text }}
        </span>
      </template>
    </template>
    <template v-else-if="previousBreadcrumb">
      &lt;
      <router-link
        v-if="previousBreadcrumb.location"
        :to="previousBreadcrumb.location"
        class="bm-breadcrumb-item"
      >
        {{ previousBreadcrumb.text }}
      </router-link>
      <span v-else class="bm-breadcrumb-item">{{
        previousBreadcrumb.text
      }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import { computed, watch } from "vue";
import { buildMenuItems } from "@/app/ui/navigation/menuItems";
import { makeRef } from "@/components/composables/componentTools";
import { useRouter } from "@/router/router";
import { screenSize } from "@/vue23switching/screenSize";

class Breadcrumb {
  text: string;
  location: Location | null;

  constructor(text: string, location: Location | null) {
    this.text = text;
    this.location = location;
  }

  public click() {}
}

interface BmBreadcrumbsProps {
  parentRoute?: string | null;
  parentRouteLocation?: Location | null;
}

const props = withDefaults(defineProps<BmBreadcrumbsProps>(), {
  parentRoute: null,
  parentRouteLocation: null,
});

const breadcrumbs = makeRef<readonly Breadcrumb[]>([]);

const { route } = useRouter();

const previousBreadcrumb = computed(() =>
  breadcrumbs.value.length > 0
    ? breadcrumbs.value[breadcrumbs.value.length - 1]
    : null
);

const traverseMenu = (
  menuItems: MenuItem[],
  targetRouteName: string
): readonly MenuItem[] | null => {
  for (const menuItem of menuItems) {
    if (menuItem.routeName === targetRouteName) {
      // return this menu item as it is the final node being searched for.
      return [menuItem];
    }

    const subPath = traverseMenu(menuItem.subMenus ?? [], targetRouteName);
    if (subPath) {
      // Have found a path underneath the item we are considering, so pre-pend the menu item
      // we are looking at to the sub-path
      return [menuItem, ...subPath];
    }
  }

  return null;
};

// This is a little bit awkward.
// Routes do not really have hierarchy, and should not really form one.
// Menus have hiearchy, but the page does not really know which menu it came from.
// To reduce manual configuration we automatically search for a menu item pointing to
// the current route.
// Menus point to routes by name (at the moment), so as we traverse we may be looking for
//  - A menu item with the given route name
//  - A menu item with the given route (mathcing by name at the moment)
//  - A given menu item
//
// We only traverse "menus", but the older menu strcuture actually represents routes in some cases
// with "menu" items which are always not visible.
const updateBreadcrumbs = () => {
  let currentRoute = route.value;

  let foundCrumbs: Breadcrumb[] = [];

  let routeToFind = currentRoute.name;
  if (props.parentRoute !== null) {
    routeToFind = props.parentRoute;
  }

  if (routeToFind) {
    let menuPath = traverseMenu(buildMenuItems(), routeToFind) ?? [];

    const lastItem = menuPath.at(-1);
    if (lastItem?.breadcrumbParentName) {
      // Legacy support - Old menu-items-mixed-with-routes can have breadcrumbParentName
      // which effectively means search for the given route not the actual page route
      // Want to remove this in future, in favour of Pages specifying a breadcrumbParent.
      // But for now just search again, for the specified route name.
      routeToFind = lastItem.breadcrumbParentName;
      menuPath = traverseMenu(buildMenuItems(), routeToFind) ?? [];
    }

    if (routeToFind === currentRoute.name) {
      // We were looking for a menu for the current page, so remove that last node from the
      // breadcrumb trail.
      menuPath = menuPath.slice(0, -1); // Returns all elements (if any) except last one
    }

    foundCrumbs = menuPath
      .filter(
        (mi) =>
          mi.translationKey &&
          (mi.visible instanceof Function ? mi.visible() : mi.visible)
      )
      .map((menuItem) => {
        const routeName =
          (menuItem.subMenus ?? []).length === 0 ? menuItem.routeName : null;
        const location = routeName ? { name: routeName } : null;

        return new Breadcrumb(menuItem.displayText(), location);
      });
  }

  breadcrumbs.value = foundCrumbs;
};

watch(route, updateBreadcrumbs, { immediate: true, deep: true });
watch(() => props.parentRoute, updateBreadcrumbs);
</script>

<style scoped lang="sass">
@import "src/sass/colours"
@import "src/sass/typography"

.bm-breadcrumb-list
  font-size: 12px
  font-family: $body-font-family
  color: $bm-color-link-text

A.bm-breadcrumb-item
  text-decoration: underline
  color: $bm-color-link-text

A.bm-breadcrumb-item:hover
  color: $bm-color-brand01
</style>
